import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeftIcon, ChevronRightIcon, PlayIcon } from '@heroicons/react/24/outline';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import PopupModal from '../components/PopupModal';
import { useCourses } from '../contexts/CourseContext';
import { useNotices } from '../contexts/NoticeContext';
import { useGalleries } from '../contexts/GalleryContext';

// 유튜브 영상 데이터
const youtubeVideos = [
  {
    id: 'u6Q_pnv5uck',
    title: '시설 둘러보기',
    description: '최신 시설과 교육 환경을 소개합니다.',
  },
  {
    id: 'eS4QV6lxFxU',
    title: '수업 현장',
    description: '실제 수업 현장을 확인해보세요.',
  },
  {
    id: 'R1xdiZHBi1U',
    title: '교육 프로그램 안내',
    description: '다양한 교육 프로그램을 만나보세요.',
  },
  {
    id: 'gSjBUCabYEg',
    title: '글로벌인재교육센터 소개',
    description: '동두천시 글로벌인재교육센터를 소개합니다.',
  },
  {
    id: 'OEPanRFhAEE',
    title: '학생 인터뷰',
    description: '교육생들의 생생한 후기입니다.',
    isShort: true,
  },
];

// 플레이스홀더 이미지 URL
const PLACEHOLDER_IMAGE = 'https://images.unsplash.com/photo-1579546929518-9e396f3cc809?w=800&h=600&fit=crop&q=80';

const Home: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [selectedProgramCategory, setSelectedProgramCategory] = useState('전체');
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [playingVideoId, setPlayingVideoId] = useState<string | null>(null);
  const { courses } = useCourses();
  const { notices } = useNotices();
  const { galleries } = useGalleries();

  // 유튜브 슬라이더 컨트롤
  const nextVideo = () => {
    setCurrentVideoIndex((prev) => (prev + 1) % youtubeVideos.length);
    setPlayingVideoId(null);
  };

  const prevVideo = () => {
    setCurrentVideoIndex((prev) => (prev - 1 + youtubeVideos.length) % youtubeVideos.length);
    setPlayingVideoId(null);
  };

  const handleVideoClick = (videoId: string) => {
    setPlayingVideoId(playingVideoId === videoId ? null : videoId);
  };

  const programCategories = [
    { id: 'all', label: '전체' },
    { id: 'group', label: '단체체험' },
    { id: 'preschool', label: '유치과정' },
    { id: 'elementary', label: '초등과정' },
    { id: 'secondary', label: '중·고등과정' },
    { id: 'adult', label: '성인과정' },
    { id: 'special', label: '특별과정' },
    { id: 'camp', label: '방학캠프' },
    { id: 'international', label: '국제교류' }
  ];

  // 필터링된 프로그램
  const filteredCourses = courses.filter(course => {
    // 비공개 강의 제외
    if (!course.is_published) return false;

    // 프로그램 페이지 노출 설정 체크
    if (!course.show_in_catalog) return false;

    // 카테고리 필터
    if (selectedProgramCategory === '전체') return true;

    const categoryMap: { [key: string]: string } = {
      '단체체험': 'group',
      '유치과정': 'preschool',
      '초등과정': 'elementary',
      '중·고등과정': 'secondary',
      '성인과정': 'adult',
      '특별과정': 'special',
      '방학캠프': 'camp',
      '국제교류': 'international'
    };
    return course.category?.includes(categoryMap[selectedProgramCategory] as any) || false;
  });

  // 홈페이지용 정렬: 특정 프로그램을 맨 앞으로
  const sortedCourses = [...filteredCourses].sort((a, b) => {
    if (a.courseName.includes('차세대 글로벌리더 하이브리드 캠프')) return -1;
    if (b.courseName.includes('차세대 글로벌리더 하이브리드 캠프')) return 1;
    return 0;
  });

  const slides = [
    {
      id: 1,
      title: "Global Talent Center",
      subtitle: "For Future Engineers",
      image: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1920&h=800&fit=crop"
    },
    {
      id: 2,
      title: "Professional Education",
      subtitle: "AI & Engineering Excellence",
      image: "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=1920&h=800&fit=crop"
    },
    {
      id: 3,
      title: "Global Competency",
      subtitle: "Building Tomorrow's Leaders",
      image: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=1920&h=800&fit=crop"
    },
    {
      id: 4,
      title: "Innovation Hub",
      subtitle: "Where Ideas Meet Reality",
      image: "https://images.unsplash.com/photo-1531482615713-2afd69097998?w=1920&h=800&fit=crop"
    },
    {
      id: 5,
      title: "Career Development",
      subtitle: "Your Success, Our Mission",
      image: "https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=1920&h=800&fit=crop"
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [slides.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  return (
    <>
      <SEO
        description="동두천시 글로벌인재교육센터 - 유아부터 성인까지 다양한 영어 교육 프로그램을 제공합니다. 수강 신청 및 프로그램 안내."
        path="/"
      />
      <PopupModal />
    <div className="relative">
      {/* Main Banner Section with Header */}
      <div className="visual_wrap relative h-screen">
        {/* Header - positioned absolutely on top of banner */}
        <Header />

        {/* Title and Counter */}
        <div className="absolute top-36 sm:top-40 md:top-48 left-5 sm:left-11 lg:left-[70px] z-20 text-white">
          <div className="main-counter mb-2 sm:mb-4">
            <div className="flex items-center gap-2 text-sm">
              <span className="font-bold text-lg sm:text-2xl">{String(currentSlide + 1).padStart(2, '0')}</span>
              <div className="w-8 sm:w-12 h-[2px] bg-white"></div>
              <span className="opacity-70">{String(slides.length).padStart(2, '0')}</span>
            </div>
          </div>
          <h2 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-bold ml-0" style={{ lineHeight: '1.35' }}>
            <strong className="text-green-400">D</strong>ongducheon<br />
            <strong className="text-green-400">G</strong>lobal <strong className="text-green-400">T</strong>alent<br />
            <strong className="text-green-400">C</strong>enter
          </h2>
        </div>

        {/* Quick Menu */}
        <div className="absolute bottom-0 left-0 right-0 z-20">
          <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 pb-4 sm:pb-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
              <Link to="/programs" className="bg-white/95 backdrop-blur-sm p-3 sm:p-5 md:p-6 rounded-lg sm:rounded-xl hover:bg-green-50 transition-all group cursor-pointer">
                <h3 className="font-bold text-sm sm:text-lg mb-1 sm:mb-2 group-hover:text-green-600">교육프로그램</h3>
                <p className="text-[10px] sm:text-sm text-gray-600 leading-tight sm:leading-normal">다양한 연령대별<br />맞춤형 교육</p>
              </Link>
              <Link to="/application" className="bg-white/95 backdrop-blur-sm p-3 sm:p-5 md:p-6 rounded-lg sm:rounded-xl hover:bg-green-50 transition-all group cursor-pointer">
                <h3 className="font-bold text-sm sm:text-lg mb-1 sm:mb-2 group-hover:text-green-600">수강신청</h3>
                <p className="text-[10px] sm:text-sm text-gray-600 leading-tight sm:leading-normal">원하는 강좌를<br />신청하세요</p>
              </Link>
              <Link to="/notice" className="bg-white/95 backdrop-blur-sm p-3 sm:p-5 md:p-6 rounded-lg sm:rounded-xl hover:bg-green-50 transition-all group cursor-pointer">
                <h3 className="font-bold text-sm sm:text-lg mb-1 sm:mb-2 group-hover:text-green-600">공지사항</h3>
                <p className="text-[10px] sm:text-sm text-gray-600 leading-tight sm:leading-normal">센터 소식 및<br />교육 일정 안내</p>
              </Link>
              <Link to="/about/facilities" className="bg-white/95 backdrop-blur-sm p-3 sm:p-5 md:p-6 rounded-lg sm:rounded-xl hover:bg-green-50 transition-all group cursor-pointer">
                <h3 className="font-bold text-sm sm:text-lg mb-1 sm:mb-2 group-hover:text-green-600">시설 안내</h3>
                <p className="text-[10px] sm:text-sm text-gray-600 leading-tight sm:leading-normal">편리하고 쾌적한<br />교육 및 체험 공간</p>
              </Link>
            </div>
          </div>
        </div>

        {/* Slider */}
        <div className="main_visual_area absolute inset-0 h-full overflow-hidden">
          <div className="mainSlide relative h-full">
            <div className="relative h-full">
              {slides.map((slide, index) => (
                <div
                  key={slide.id}
                  className={`absolute inset-0 transition-opacity duration-1000 ${
                    index === currentSlide ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  <img
                    src={slide.image}
                    alt={slide.title}
                    className="w-full h-full object-cover object-[center_30%]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50"></div>
                </div>
              ))}
            </div>

            {/* Slide Controls */}
            <div className="absolute top-1/2 -translate-y-1/2 left-2 sm:left-4 md:left-8 right-2 sm:right-4 md:right-8 flex justify-between">
              <button
                onClick={prevSlide}
                className="bg-white/20 backdrop-blur-sm text-white p-2 sm:p-3 rounded-full hover:bg-white/30 transition-all"
              >
                <ChevronLeftIcon className="h-5 w-5 sm:h-6 sm:w-6" />
              </button>
              <button
                onClick={nextSlide}
                className="bg-white/20 backdrop-blur-sm text-white p-2 sm:p-3 rounded-full hover:bg-white/30 transition-all"
              >
                <ChevronRightIcon className="h-5 w-5 sm:h-6 sm:w-6" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Section */}
      <div className="py-10 sm:py-16 md:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* 프로그램 소개 - Full Width */}
          <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 md:p-8 mb-8 sm:mb-10 md:mb-12">
            <div className="flex justify-between items-center mb-4 sm:mb-6">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800">프로그램 소개</h2>
              <Link
                to={
                  selectedProgramCategory === '전체'
                    ? '/programs'
                    : `/programs/category/${programCategories.find(cat => cat.label === selectedProgramCategory)?.id}`
                }
                onClick={() => window.scrollTo(0, 0)}
                className="text-sm text-green-600 hover:text-green-700 font-medium flex items-center gap-1"
              >
                더보기
                <ChevronRightIcon className="h-4 w-4" />
              </Link>
            </div>

            {/* Category Filter */}
            <div className="mb-4 sm:mb-6 overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
              <div className="flex gap-2 min-w-max sm:min-w-0 sm:flex-wrap">
                {programCategories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedProgramCategory(category.label)}
                    className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                      selectedProgramCategory === category.label
                        ? 'bg-green-600 text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {category.label}
                  </button>
                ))}
              </div>
            </div>

            {filteredCourses.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
                {sortedCourses.slice(0, 3).map((course) => (
                  <Link
                    key={course.id}
                    to={`/courses/${course.id}`}
                    className="block group"
                  >
                    {/* 썸네일 */}
                    <div className="relative mb-3 sm:mb-4 rounded-lg overflow-hidden">
                      {course.courseImage ? (
                        <img
                          src={course.courseImage}
                          alt={course.courseName}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-40 sm:h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className="w-full h-40 sm:h-48 bg-gray-200 flex items-center justify-center">
                          <span className="text-gray-400 text-xs sm:text-sm">이미지 없음</span>
                        </div>
                      )}
                    </div>

                    <div className="mb-2 sm:mb-3">
                      <span className="inline-block bg-gray-100 text-gray-700 text-xs font-medium px-2 sm:px-3 py-1 rounded-full">
                        교육
                      </span>
                    </div>

                    <h3 className="text-base sm:text-lg font-bold text-gray-800 mt-1 group-hover:text-green-600 transition-colors line-clamp-1">
                      {course.courseName}
                    </h3>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 sm:py-16">
                <p className="text-gray-500 text-base sm:text-lg">서비스 준비중입니다.</p>
              </div>
            )}
          </div>

          {/* 공지사항 + 유튜브 & 갤러리 Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 md:gap-12 items-stretch">
            {/* 좌측: 공지사항 + 유튜브 */}
            <div className="space-y-6">
              {/* 공지사항 */}
              <div className="bg-white rounded-lg shadow-sm p-4 sm:p-5">
                <div className="flex justify-between items-center mb-2">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-800">공지사항</h2>
                  <Link
                    to="/notice"
                    onClick={() => window.scrollTo(0, 0)}
                    className="text-sm text-green-600 hover:text-green-700 font-medium flex items-center gap-1"
                  >
                    더보기
                    <ChevronRightIcon className="h-4 w-4" />
                  </Link>
                </div>

                <ul className="divide-y divide-gray-100">
                  {notices.slice(0, 5).map((notice) => (
                    <li key={notice.id}>
                      <Link
                        to={`/notice/${notice.id}`}
                        className="block py-3 sm:py-4 px-1 sm:px-2 hover:bg-gray-50 transition-colors group"
                      >
                        <div className="flex justify-between items-center gap-2 sm:gap-4">
                          <h3 className="text-sm sm:text-base font-medium text-gray-800 group-hover:text-green-600 transition-colors flex-1 truncate">
                            {notice.title}
                          </h3>
                          <p className="text-xs sm:text-sm text-gray-400 whitespace-nowrap">
                            {new Date(notice.created_at).toLocaleDateString('ko-KR')}
                          </p>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* 유튜브 영상 */}
              <div className="bg-white rounded-lg shadow-sm p-4 sm:p-5 overflow-hidden">
                {/* 영상 갤러리 타이틀 */}
                <h2 className="text-xl sm:text-2xl font-bold text-gray-800">
                  영상 갤러리
                </h2>

                {/* 캐러셀 영상 */}
                <div className="relative mt-1">
                  {/* 캐러셀 트랙 */}
                  <div className="overflow-hidden">
                    <div
                      className="flex items-center transition-transform duration-300 ease-out"
                      style={{ transform: `translateX(calc(-${currentVideoIndex * 88}% + 6%))` }}
                    >
                      {youtubeVideos.map((video, index) => {
                        const isActive = currentVideoIndex === index;

                        return (
                          <div
                            key={video.id}
                            className="flex-shrink-0 w-[88%] px-1"
                            onClick={() => {
                              if (!isActive) {
                                setCurrentVideoIndex(index);
                                setPlayingVideoId(null);
                              }
                            }}
                          >
                            <div
                              className={`relative rounded-xl overflow-hidden bg-gray-900 transition-all duration-300 ${
                                video.isShort ? 'aspect-[9/16] max-w-[180px] mx-auto' : 'aspect-video'
                              } ${isActive ? 'scale-100 opacity-100' : 'scale-95 opacity-50 cursor-pointer'}`}
                            >
                              {isActive && playingVideoId === video.id ? (
                                <iframe
                                  src={`https://www.youtube.com/embed/${video.id}?autoplay=1`}
                                  title={video.title}
                                  className="absolute inset-0 w-full h-full"
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                  allowFullScreen
                                />
                              ) : (
                                <>
                                  <img
                                    src={`https://img.youtube.com/vi/${video.id}/maxresdefault.jpg`}
                                    alt={video.title}
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                      (e.target as HTMLImageElement).src = `https://img.youtube.com/vi/${video.id}/hqdefault.jpg`;
                                    }}
                                  />
                                  {isActive && (
                                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleVideoClick(video.id);
                                        }}
                                        className="w-12 h-12 sm:w-14 sm:h-14 bg-white/90 rounded-full flex items-center justify-center hover:bg-white hover:scale-110 transition-all duration-300 shadow-lg"
                                      >
                                        <PlayIcon className="w-5 h-5 sm:w-6 sm:h-6 text-green-600 ml-0.5" />
                                      </button>
                                    </div>
                                  )}
                                  {!isActive && (
                                    <div className="absolute inset-0 bg-black/20"></div>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* 좌우 화살표 */}
                  {currentVideoIndex > 0 && (
                    <button
                      onClick={prevVideo}
                      className="absolute left-0 top-1/2 -translate-y-1/2 z-20 p-2 bg-white/90 hover:bg-white rounded-full shadow-lg transition-colors"
                    >
                      <ChevronLeftIcon className="w-5 h-5 text-gray-700" />
                    </button>
                  )}
                  {currentVideoIndex < youtubeVideos.length - 1 && (
                    <button
                      onClick={nextVideo}
                      className="absolute right-0 top-1/2 -translate-y-1/2 z-20 p-2 bg-white/90 hover:bg-white rounded-full shadow-lg transition-colors"
                    >
                      <ChevronRightIcon className="w-5 h-5 text-gray-700" />
                    </button>
                  )}
                </div>

                {/* 도트 네비게이션 */}
                <div className="flex justify-center items-center gap-1.5 mt-2">
                  {youtubeVideos.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setCurrentVideoIndex(index);
                        setPlayingVideoId(null);
                      }}
                      className={`h-1.5 rounded-full transition-all ${
                        currentVideoIndex === index ? 'bg-green-600 w-4' : 'bg-gray-300 hover:bg-gray-400 w-1.5'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* 우측: 갤러리 */}
            <div className="bg-white rounded-lg shadow-sm p-4 sm:p-5 flex flex-col">
              <div className="flex justify-between items-center mb-3 sm:mb-4">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-800">갤러리</h2>
                <Link
                  to="/gallery"
                  onClick={() => window.scrollTo(0, 0)}
                  className="text-sm text-green-600 hover:text-green-700 font-medium flex items-center gap-1"
                >
                  더보기
                  <ChevronRightIcon className="h-4 w-4" />
                </Link>
              </div>

              <div className="grid grid-cols-2 gap-4 sm:gap-6 flex-1">
                {galleries.length > 0 ? (
                  galleries.slice(0, 4).map((gallery) => (
                    <Link
                      key={gallery.id}
                      to={`/gallery/${gallery.id}`}
                      className="group cursor-pointer block"
                    >
                      <div className="relative overflow-hidden rounded-lg aspect-square bg-gray-200 mb-2">
                        <img
                          src={gallery.thumbnail_url || gallery.image_url || PLACEHOLDER_IMAGE}
                          alt={gallery.title}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300"></div>
                      </div>
                      <h3 className="text-xs sm:text-sm font-medium text-gray-800 group-hover:text-green-600 transition-colors line-clamp-2">
                        {gallery.title}
                      </h3>
                    </Link>
                  ))
                ) : (
                  /* 갤러리가 없을 때 플레이스홀더 */
                  [1, 2, 3, 4].map((item) => (
                    <div key={item} className="group cursor-pointer">
                      <div className="relative overflow-hidden rounded-lg aspect-square bg-gradient-to-br from-gray-200 to-gray-100">
                        <div className="absolute inset-0 flex items-center justify-center">
                          <svg className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
    </>
  );
};

export default Home;